import React, { useCallback, useEffect, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import {
  buildKycExpiryBody,
  kycReviewReadEndpoint,
  kycReviewWriteEndpoint,
  useKycReviewRequest
} from "./kycReviewApi";

/**
 * Update KYC Information (2.8.0) — edit `customer_unitholders.kyc_expired_at` per unitholder.
 *
 * The date is what be-customer compares against to decide whether the in-app Update KYC screen is
 * due. Nothing but an in-app confirmation writes it today, so without this an operator cannot set
 * up a test case or correct a date that landed wrong.
 *
 * Empty input is not "no change": it clears the column back to NULL, which be-customer reads as
 * "we do not know" and therefore never as due. The screen says so, because the difference matters
 * and is not guessable from an empty box.
 */

/** `datetime-local` wants `YYYY-MM-DDTHH:mm` in local time; the API speaks ISO/UTC. */
function toInputValue(iso) {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const pad = n => String(n).padStart(2, "0");
  return (
    date.getFullYear() +
    "-" +
    pad(date.getMonth() + 1) +
    "-" +
    pad(date.getDate()) +
    "T" +
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes())
  );
}

function formatDisplay(iso) {
  if (!iso) {
    return "—";
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? String(iso) : date.toLocaleString();
}

const KycExpiryCard = ({ customerUuid }) => {
  const requestKycReview = useKycReviewRequest();

  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");
  const [savedMessage, setSavedMessage] = useState("");

  const applyRows = useCallback(list => {
    setRows(list);
    const next = {};
    list.forEach(row => {
      next[row.id] = toInputValue(row.kycExpiredAt);
    });
    setDrafts(next);
  }, []);

  const load = useCallback(async () => {
    if (!customerUuid) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await requestKycReview(kycReviewReadEndpoint(customerUuid));
      if (result.signedOut) {
        return;
      }
      applyRows(Array.isArray(result.data) ? result.data : []);
    } catch (err) {
      setError(
        err && err.status
          ? `Could not load KYC review dates (HTTP ${err.status}${
              err.detail ? ` — ${err.detail}` : ""
            })`
          : "Could not load KYC review dates."
      );
    } finally {
      setLoading(false);
    }
  }, [applyRows, customerUuid, requestKycReview]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async row => {
    setSavingId(row.id);
    setError("");
    setSavedMessage("");
    try {
      const draft = drafts[row.id];
      const endpoint = kycReviewWriteEndpoint(customerUuid);
      const result = await requestKycReview(endpoint, {
        method: "PATCH",
        body: buildKycExpiryBody(
          row.unitholderId,
          draft ? new Date(draft).toISOString() : null
        )
      });
      if (result.signedOut) {
        return;
      }
      applyRows(Array.isArray(result.data) ? result.data : []);
      setSavedMessage(
        draft
          ? `Saved. ${row.unitholderNo || row.unitholderId} is next due ${formatDisplay(
              new Date(draft).toISOString()
            )}.`
          : `Cleared. ${row.unitholderNo ||
              row.unitholderId} has no expiry date, so the app will not ask for a KYC update.`
      );
    } catch (err) {
      setError(
        err && err.status
          ? `Save failed (HTTP ${err.status}${err.detail ? ` — ${err.detail}` : ""})`
          : "Save failed."
      );
    } finally {
      setSavingId(null);
    }
  };

  if (!customerUuid) {
    return null;
  }

  return (
    <div className="kyc-expiry-card">
      <div className="kyc-expiry-card-head">
        <p className="kyc-expiry-card-title">KYC REVIEW</p>
        {loading ? <CircularProgress size={16} /> : null}
      </div>

      {error ? <p className="kyc-expiry-card-error">{error}</p> : null}
      {savedMessage ? (
        <p className="kyc-expiry-card-saved">{savedMessage}</p>
      ) : null}

      {!loading && rows.length === 0 ? (
        <p className="kyc-expiry-card-empty">
          This customer has no unitholder rows.
        </p>
      ) : null}

      {rows.map(row => (
        <div className="kyc-expiry-row" key={row.id}>
          <div className="kyc-expiry-row-who">
            <span className="kyc-expiry-row-no">
              {row.unitholderNo || row.unitholderId || "(no unitholder id)"}
            </span>
            <span className="kyc-expiry-row-name">{row.unitholderName}</span>
          </div>

          <div className="kyc-expiry-row-dates">
            <span>Last updated: {formatDisplay(row.kycUpdatedAt)}</span>
            <span>Account approved: {formatDisplay(row.reviewAt)}</span>
          </div>

          <div className="kyc-expiry-row-edit">
            <label htmlFor={`kyc-expired-at-${row.id}`}>
              Next KYC review date
            </label>
            <input
              id={`kyc-expired-at-${row.id}`}
              type="datetime-local"
              value={drafts[row.id] || ""}
              onChange={event =>
                setDrafts({ ...drafts, [row.id]: event.target.value })
              }
            />
            <button
              type="button"
              disabled={savingId === row.id}
              onClick={() => save(row)}
            >
              {savingId === row.id ? "SAVING..." : "SAVE"}
            </button>
          </div>

          <p className="kyc-expiry-row-note">
            Leave the box empty and save to clear the date. An empty date means
            "not known", which never makes the app ask for an update.
          </p>
        </div>
      ))}
    </div>
  );
};

export default KycExpiryCard;
